import React, { useCallback, useMemo, useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { ArrowBack, CopyAll, Upload } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import useCurrentUser from '~/hooks/useCurrentUser';

import {
  MakeMeAnOfferActionType,
  MakeMeAnOfferCloneFlashMeta,
  MakeMeAnOfferCloneLpcMeta,
  MakeMeAnOfferErrorMeta,
  MakeMeAnOfferUpsertFlashMeta,
  MakeMeAnOfferUpsertLpcMeta,
  PreviewCloneOfferResponse,
  PreviewProdOfferResponse,
  ProgressStatusResponse,
  getProgressStatuses,
  makeMeAnOffer,
  previewCloneOffer,
  previewProdOffer,
} from '~/services/AccommodationService';

import asyncPoll from '~/utils/asyncPoll';

import MakeMeAnOfferCreateStatus, { MakeMeAnOfferCreateStatusProps } from './MakeMeAnOfferCreateStatus';

function isUpsertFlashMeta(meta: ProgressStatusResponse['meta']): meta is MakeMeAnOfferUpsertFlashMeta {
  return 'actionType' in meta && meta.actionType === 'upsert-prod-flash-offer';
}

function isUpsertLpcMeta(meta: ProgressStatusResponse['meta']): meta is MakeMeAnOfferUpsertLpcMeta {
  return 'actionType' in meta && meta.actionType === 'upsert-prod-lpc-offer';
}

function isCloneFlashMeta(meta: ProgressStatusResponse['meta']): meta is MakeMeAnOfferCloneFlashMeta {
  return 'actionType' in meta && meta.actionType === 'clone-flash-offer';
}

function isCloneLpcMeta(meta: ProgressStatusResponse['meta']): meta is MakeMeAnOfferCloneLpcMeta {
  return 'actionType' in meta && meta.actionType === 'clone-lpc-offer';
}

function isErrorMeta(meta: ProgressStatusResponse['meta']): meta is MakeMeAnOfferErrorMeta {
  return 'error' in meta;
}

const MODAL_STYLE = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '30%',
  minWidth: 400,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 3,
  overflowY: 'auto',
};

interface MakeMeAnOfferFormData {
  offerId?: string;
  weekendInflationPercent?: number;
  seasonalInflationPercent?: number;
  vendorBookingEmail?: string;
  destroyDate?: Dayjs | null;
}

enum MAKE_ME_AN_OFFER_SETUP_STEPS {
  CHOOSE_TYPE,
  FILL_FORM,
  PREVIEW_OFFER,
  PROCESSING,
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function MakeMeAnOfferModal({ open, onClose }: Props) {
  const { user } = useCurrentUser();

  const [currentStep, setCurrentStep] = useState<MAKE_ME_AN_OFFER_SETUP_STEPS>(
    MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE,
  );
  const [selectedType, setSelectedType] = useState<'copy' | 'import' | null>(null);
  const [loading, setLoading] = useState(false);
  const [enableDestroyDate, setEnableDestroyDate] = useState(true);

  const { enqueueSnackbar } = useSnackbar();

  const twoWeeksFromNow = useMemo(() => dayjs().add(2, 'week'), []);

  const makeMeAnOfferInitialValues: MakeMeAnOfferFormData = {
    offerId: '',
    weekendInflationPercent: 0,
    seasonalInflationPercent: 0,
    vendorBookingEmail: user?.email,
    destroyDate: twoWeeksFromNow,
  };

  const [makeMeAnOfferStatus, setMakeMeAnOfferStatus] = useState<MakeMeAnOfferCreateStatusProps['status']>({});
  const [actionType, setActionType] = useState<MakeMeAnOfferActionType | null>(null);

  const [offerUrl, setOfferUrl] = useState<string | null>(null);
  const [createdOfferName, setCreatedOfferName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [offerPreview, setOfferPreview] = useState<PreviewProdOfferResponse | null>(null);
  const [copyOfferPreview, setCopyOfferPreview] = useState<PreviewCloneOfferResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { control, handleSubmit, watch } = useForm<MakeMeAnOfferFormData>({
    mode: 'onBlur',
    defaultValues: makeMeAnOfferInitialValues,
  });

  const offerId = watch('offerId');

  const onModalClose = useCallback(() => {
    setOfferUrl(null);
    setCreatedOfferName(null);
    setMakeMeAnOfferStatus({});
    setActionType(null);
    setErrorMessage(null);
    setOfferPreview(null);
    setCopyOfferPreview(null);
    setPreviewError(null);
    setSelectedType(null);
    setEnableDestroyDate(true);
    setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE);
    onClose();
  }, [onClose]);

  const handleSearchImportOffer = useCallback(
    async (offerId: string) => {
      if (!offerId) {
        setPreviewError('Please enter an offer ID');
        return;
      }

      setPreviewLoading(true);
      setPreviewError(null);

      try {
        const preview = await previewProdOffer(offerId);
        setOfferPreview(preview);

        if (preview.canImport) {
          setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.PREVIEW_OFFER);
        } else {
          setPreviewError(preview.validationMessages.join(' '));
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to preview offer';
        setPreviewError(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
      } finally {
        setPreviewLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const handleSearchCopyOffer = useCallback(
    async (offerId: string) => {
      if (!offerId) {
        setPreviewError('Please enter an offer ID');
        return;
      }

      setPreviewLoading(true);
      setPreviewError(null);

      try {
        const preview = await previewCloneOffer(offerId);
        setCopyOfferPreview(preview);
        setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.PREVIEW_OFFER);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to fetch offer';
        setPreviewError(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
      } finally {
        setPreviewLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const handleSubmission = useCallback<SubmitHandler<MakeMeAnOfferFormData>>(
    async (data) => {
      setLoading(true);
      setErrorMessage(null);
      setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.PROCESSING);

      let payload;
      if (selectedType === 'import' && offerPreview) {
        if (offerPreview.importMode === 'upsert-prod-lpc-offer') {
          payload = {
            type: 'upsert-prod-lpc-offer' as const,
            offerId: data.offerId,
            weekendInflationPercent: Number(data.weekendInflationPercent),
            seasonalInflationPercent: Number(data.seasonalInflationPercent),
          };
        } else {
          payload = {
            type: 'upsert-prod-flash-offer' as const,
            offerId: data.offerId,
          };
        }
      } else if (selectedType === 'copy' && copyOfferPreview) {
        if (copyOfferPreview.cloneMode === 'clone-lpc-offer') {
          payload = {
            type: 'clone-lpc-offer' as const,
            offerId: data.offerId,
            vendorBookingEmail: data.vendorBookingEmail,
            destroyDate: enableDestroyDate && data.destroyDate ? data.destroyDate.toISOString() : undefined,
            weekendInflationPercent: Number(data.weekendInflationPercent),
            seasonalInflationPercent: Number(data.seasonalInflationPercent),
          };
        } else {
          payload = {
            type: 'clone-flash-offer' as const,
            offerId: data.offerId,
            vendorBookingEmail: data.vendorBookingEmail,
            destroyDate: enableDestroyDate && data.destroyDate ? data.destroyDate.toISOString() : undefined,
          };
        }
      } else {
        const errorMsg = 'Invalid flow state - please restart the process';
        setErrorMessage(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
        setLoading(false);
        return;
      }

      try {
        const { result } = await makeMeAnOffer(payload);
        const jobId = result.id;

        try {
          await asyncPoll({
            apiCall: () => getProgressStatuses(jobId),
            validateFunction: async ({ result }) => {
              const makeTestOffer = result.find((status) => status.type === 'makeTestOffer');
              if (!makeTestOffer) {
                return false;
              }

              const meta = makeTestOffer.meta;

              if (isUpsertLpcMeta(meta)) {
                setActionType('upsert-prod-lpc-offer');
                setMakeMeAnOfferStatus({
                  validateOffer: meta.validateOffer ?? 'awaiting',
                  validateProperty: meta.validateProperty ?? 'awaiting',
                  validateVendor: meta.validateVendor ?? 'awaiting',
                  upsertVendor: meta.upsertVendor ?? 'awaiting',
                  upsertProperty: meta.upsertProperty ?? 'awaiting',
                  upsertOffer: meta.upsertOffer ?? 'awaiting',
                  waitForChannelManagerSync: meta.waitForChannelManagerSync ?? 'awaiting',
                  insertARI: meta.insertARI ?? 'awaiting',
                  resyncOffer: meta.resyncOffer ?? 'awaiting',
                });
              } else if (isUpsertFlashMeta(meta)) {
                setActionType('upsert-prod-flash-offer');
                setMakeMeAnOfferStatus({
                  validateOffer: meta.validateOffer ?? 'awaiting',
                  validateProperty: meta.validateProperty ?? 'awaiting',
                  validateVendor: meta.validateVendor ?? 'awaiting',
                  upsertVendor: meta.upsertVendor ?? 'awaiting',
                  upsertProperty: meta.upsertProperty ?? 'awaiting',
                  upsertOffer: meta.upsertOffer ?? 'awaiting',
                  resyncOffer: meta.resyncOffer ?? 'awaiting',
                });
              } else if (isCloneLpcMeta(meta)) {
                setActionType('clone-lpc-offer');
                setMakeMeAnOfferStatus({
                  validateOffer: meta.validateOffer ?? 'awaiting',
                  cloneVendor: meta.cloneVendor ?? 'awaiting',
                  cloneProperty: meta.cloneProperty ?? 'awaiting',
                  cloneOffer: meta.cloneOffer ?? 'awaiting',
                  waitForChannelManagerSync: meta.waitForChannelManagerSync ?? 'awaiting',
                  insertARI: meta.insertARI ?? 'awaiting',
                  resyncOffer: meta.resyncOffer ?? 'awaiting',
                });
              } else if (isCloneFlashMeta(meta)) {
                setActionType('clone-flash-offer');
                setMakeMeAnOfferStatus({
                  validateOffer: meta.validateOffer ?? 'awaiting',
                  cloneVendor: meta.cloneVendor ?? 'awaiting',
                  cloneProperty: meta.cloneProperty ?? 'awaiting',
                  cloneOffer: meta.cloneOffer ?? 'awaiting',
                  resyncOffer: meta.resyncOffer ?? 'awaiting',
                });
              }

              if (makeTestOffer.state === 'completed') {
                setLoading(false);
                if (isUpsertLpcMeta(meta) || isUpsertFlashMeta(meta)) {
                  setOfferUrl(`/offers/${meta.offerId}`);
                  setCreatedOfferName(meta.offerName);
                } else if (isCloneLpcMeta(meta) || isCloneFlashMeta(meta)) {
                  const clonedOfferId = meta.newOfferIdSalesforceExternal;
                  if (clonedOfferId) {
                    setOfferUrl(`/offers/${clonedOfferId}`);
                    setCreatedOfferName(clonedOfferId);
                  } else {
                    const errorMsg = 'Offer cloned but no offer ID was returned';
                    setErrorMessage(errorMsg);
                    enqueueSnackbar(errorMsg, { variant: 'error' });
                  }
                }
                return true;
              } else if (makeTestOffer.state === 'failed') {
                const errorMsg = makeTestOffer.message || 'Failed to create offer';
                const metaError = isErrorMeta(makeTestOffer.meta) ? makeTestOffer.meta.error : undefined;
                const displayError = metaError || errorMsg;

                setErrorMessage(displayError);
                enqueueSnackbar(displayError, { variant: 'error' });
                setLoading(false);
                return true;
              } else {
                return false;
              }
            },
            maxTime: 300000, // 5 minutes
          });
        } catch (e) {
          const errorMsg = e instanceof Error ? e.message : 'An unexpected error occurred';
          setErrorMessage(errorMsg);
          enqueueSnackbar(errorMsg, { variant: 'error' });
          setLoading(false);
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to submit offer creation request';
        setErrorMessage(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
        setLoading(false);
      }
    },
    [selectedType, offerPreview, copyOfferPreview, enableDestroyDate, enqueueSnackbar],
  );

  const handleCancel = useCallback(() => {
    if (currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.PROCESSING && loading) {
      // Don't allow cancel during processing
      return;
    }
    setSelectedType(null);
    setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE);
  }, [currentStep, loading]);

  return (
    <Modal open={open} onClose={onModalClose}>
      <Box sx={MODAL_STYLE}>
        <form onSubmit={handleSubmit(handleSubmission)}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            Make me an offer
          </Typography>

          {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE && (
            <Box sx={{ py: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
                Choose how you want to create an offer
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => {
                      setSelectedType('copy');
                      setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.FILL_FORM);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CopyAll sx={{ fontSize: 48, color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
                        Copy Offer
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Clone an existing staging offer with all its details
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => {
                      setSelectedType('import');
                      setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.FILL_FORM);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Upload sx={{ fontSize: 48, color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
                        Import from Prod
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Import a production offer directly into staging
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.FILL_FORM && (
            <Box>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => {
                  setSelectedType(null);
                  setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE);
                }}
                sx={{ mb: 2 }}
              >
                Back
              </Button>

              {selectedType === 'copy' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Copy Staging Offer
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        Enter the Salesforce ID of the staging offer you want to copy.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="offerId"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Staging Offer ID"
                            fullWidth
                            required
                            error={!!previewError}
                            helperText={previewError || 'Enter Salesforce ID and click Search'}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Button
                        variant="contained"
                        onClick={() => handleSearchCopyOffer(offerId ?? '')}
                        disabled={previewLoading}
                      >
                        {previewLoading ? 'Searching...' : 'Search Offer'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {selectedType === 'import' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Import Production Offer
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        Enter the Salesforce ID of the production offer you want to import.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="offerId"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Production Offer ID"
                            fullWidth
                            required
                            error={!!previewError}
                            helperText={previewError || 'Enter Salesforce ID and click Search'}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Button
                        variant="contained"
                        onClick={() => handleSearchImportOffer(offerId ?? '')}
                        disabled={previewLoading}
                      >
                        {previewLoading ? 'Searching...' : 'Search Offer'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={handleCancel}>Cancel</Button>
              </Box>
            </Box>
          )}

          {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.PREVIEW_OFFER && (offerPreview || copyOfferPreview) && (
            <Box>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => {
                  setOfferPreview(null);
                  setCopyOfferPreview(null);
                  setPreviewError(null);
                  setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.FILL_FORM);
                }}
                sx={{ mb: 2 }}
              >
                Change Offer
              </Button>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Offer Preview
              </Typography>

              {/* Import Flow Preview */}
              {selectedType === 'import' && offerPreview && (
                <>
                  <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {offerPreview.offer.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Offer ID:</strong> {offerPreview.offer.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Type:</strong> {offerPreview.offer.type === 'hotel' ? 'Flash Offer' : 'LPC Offer'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Property:</strong> {offerPreview.property.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Channel Manager:</strong> {offerPreview.property.channelManager || 'Self-managed'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Vendor:</strong> {offerPreview.vendor.name}
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* What Will Happen */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      What will happen:
                    </Typography>
                    {offerPreview.validationMessages.map((msg, idx) => (
                      <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                        • {msg}
                      </Typography>
                    ))}
                  </Box>

                  {/* Pricing Options - Only for LPC offers */}
                  {offerPreview.importMode === 'upsert-prod-lpc-offer' && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Dynamic Pricing Options
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Configure pricing for the imported LPC offer. Weekend inflation applies to Friday-Saturday.
                        Seasonal inflation applies during NSW school holidays.
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Controller
                            name="weekendInflationPercent"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="number"
                                label="Weekend Inflation (%)"
                                fullWidth
                                sx={{ mb: 2 }}
                                helperText="Fri-Sat price increase"
                                InputProps={{ inputProps: { min: 0, max: 100 } }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                          <Controller
                            name="seasonalInflationPercent"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="number"
                                label="Seasonal Inflation (%)"
                                fullWidth
                                sx={{ mb: 2 }}
                                helperText="NSW school holidays increase"
                                InputProps={{ inputProps: { min: 0, max: 100 } }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </>
              )}

              {/* Copy Flow Preview */}
              {selectedType === 'copy' && copyOfferPreview && (
                <>
                  <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {copyOfferPreview.offer.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Offer ID:</strong> {copyOfferPreview.offer.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Type:</strong> {copyOfferPreview.offer.type === 'hotel' ? 'Flash Offer' : 'LPC Offer'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Property:</strong> {copyOfferPreview.property.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Channel Manager:</strong> {copyOfferPreview.property.channelManager || 'Self-managed'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Vendor:</strong> {copyOfferPreview.vendor.name}
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* What Will Happen */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      What will happen:
                    </Typography>
                    {copyOfferPreview.validationMessages.map((message, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {message}
                      </Typography>
                    ))}
                    {!copyOfferPreview.canClone && (
                      <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 500 }}>
                        This offer cannot be cloned. Please check the messages above.
                      </Typography>
                    )}
                  </Box>

                  {/* Clone Options */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Clone Options
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Controller
                          name="vendorBookingEmail"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Vendor booking email (optional)"
                              fullWidth
                              sx={{ mb: 2 }}
                              helperText="Defaults to your email if blank"
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={enableDestroyDate}
                              onChange={(e) => setEnableDestroyDate(e.target.checked)}
                            />
                          }
                          label="Set destroy date (auto-delete this offer after a period)"
                        />
                      </Grid>
                      {enableDestroyDate && (
                        <Grid size={{ xs: 12 }}>
                          <Controller
                            name="destroyDate"
                            control={control}
                            render={({ field: { onChange, value, ...restField } }) => (
                              <DatePicker
                                {...restField}
                                label="Destroy Date"
                                value={value}
                                onChange={onChange}
                                minDate={dayjs()}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    sx: { mb: 2 },
                                    helperText: 'Offer will be marked for cleanup after this date',
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Pricing Options - Only for LPC offers */}
                  {copyOfferPreview.cloneMode === 'clone-lpc-offer' && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Dynamic Pricing Options
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Configure pricing for the cloned LPC offer. Weekend inflation applies to Friday-Saturday.
                        Seasonal inflation applies during NSW school holidays.
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Controller
                            name="weekendInflationPercent"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="number"
                                label="Weekend Inflation (%)"
                                fullWidth
                                sx={{ mb: 2 }}
                                helperText="Fri-Sat price increase"
                                InputProps={{ inputProps: { min: 0, max: 100 } }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                          <Controller
                            name="seasonalInflationPercent"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="number"
                                label="Seasonal Inflation (%)"
                                fullWidth
                                sx={{ mb: 2 }}
                                helperText="NSW school holidays increase"
                                InputProps={{ inputProps: { min: 0, max: 100 } }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </>
              )}

              {/* Action Buttons */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading || (selectedType === 'copy' && copyOfferPreview && !copyOfferPreview.canClone)}
                >
                  {loading ? 'Creating...' : 'Create Offer'}
                </Button>
              </Box>
            </Box>
          )}

          {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.PROCESSING && (
            <Box sx={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {loading ? 'Creating Your Offer...' : offerUrl ? 'Success!' : 'Process Failed'}
              </Typography>

              <Box sx={{ flex: 1 }}>
                <MakeMeAnOfferCreateStatus
                  name={createdOfferName}
                  status={makeMeAnOfferStatus}
                  offerUrl={offerUrl}
                  errorMessage={errorMessage}
                  actionType={actionType}
                />
              </Box>

              {!loading && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  {offerUrl && (
                    <Button variant="contained" color="primary" href={offerUrl} target="_blank">
                      View Offer
                    </Button>
                  )}
                  <Button onClick={onModalClose}>Close</Button>
                </Box>
              )}
            </Box>
          )}
        </form>
      </Box>
    </Modal>
  );
}
